import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useOnboarding } from "../../../contexts/OnboardingContext";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const ChildAgeStep = () => {
  const { username, childAge, setChildAge, nextStep } = useOnboarding();
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById("onboarding-continue-slot");
      if (el) setPortalTarget(el);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const ageOptions = [
    { icon: "👶", label: "0–2", value: "0-2", tag: "Toddler" },
    { icon: "🧒", label: "3–5", value: "3-5", tag: "Preschool" },
    { icon: "🎒", label: "6–9", value: "6-9", tag: "School Age" },
    { icon: "✨", label: "Other", value: "other", tag: "Custom" },
  ];

  const handleSelect = (value) => setChildAge(value);
  const handleNext = () => { if (childAge) nextStep(); };
  const canContinue = !!childAge;

  const continueButton = (
    <button
      onClick={handleNext}
      disabled={!canContinue}
      className="inline-flex items-center gap-2 transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 15,
        border: "none",
        borderRadius: 999,
        padding: "12px 22px",
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
        cursor: canContinue ? "pointer" : "not-allowed",
      }}
    >
      Continue
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      {/* Panda + Speech Bubble (centered) */}
      <div className="flex flex-col items-center text-center mb-10 w-full">
        <div className="relative w-28 h-28 sm:w-32 sm:h-32 mb-4">
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,101,248,0.15)", filter: "blur(28px)", borderRadius: "50%", transform: "scale(1.4)" }} />
          <img
            src="/assets/pixley-panda-nobg.png"
            alt="Pixley Panda"
            className="relative w-full h-full object-contain z-10"
            onError={(e) => { e.target.src = "/assets/pixley-panda.png"; }}
            style={{ filter: "drop-shadow(0px 6px 12px rgba(11,26,51,0.18))" }}
          />
        </div>
        <div
          className="relative px-6 py-4 sm:px-8 sm:py-5"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 22,
            boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
          }}
        >
          <div
            className="absolute z-0"
            style={{
              top: -8,
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: 16,
              height: 16,
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRight: "none",
              borderBottom: "none",
            }}
          />
          <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Age
          </span>
          <h1
            className="relative z-10"
            style={{
              ...FREDOKA,
              color: "#0B1A33",
              fontWeight: 700,
              fontSize: "clamp(1.3rem, 2.4vw, 1.8rem)",
              textTransform: "uppercase",
              letterSpacing: "-0.01em",
              margin: "4px 0 0",
            }}
          >
            How old are you{username ? `, ${username}` : ""}?
          </h1>
        </div>
      </div>

      {/* Age Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full mb-6">
        {ageOptions.map((option) => {
          const isSelected = childAge === option.value;
          return (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className="group relative flex flex-col items-center gap-3 transition-all active:scale-[0.97]"
              style={{
                background: isSelected ? "linear-gradient(180deg, #EAF3FF 0%, #DBEAFE 100%)" : "#FFFFFF",
                border: isSelected ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                borderRadius: 22,
                padding: "20px 14px",
                boxShadow: isSelected
                  ? "0px 5px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.20)"
                  : "0px 3px 0px rgba(11,26,51,0.06)",
                cursor: "pointer",
              }}
            >
              {isSelected && (
                <div
                  className="absolute"
                  style={{
                    top: 8,
                    right: 8,
                    width: 22,
                    height: 22,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 2px 0px #073B8E",
                  }}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              <span style={{ fontSize: 44 }}>{option.icon}</span>
              <div className="flex flex-col items-center">
                <span style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 17, lineHeight: 1 }}>
                  {option.label}
                </span>
                <span
                  style={{
                    ...FREDOKA,
                    color: isSelected ? "#0065F8" : "#94A3B8",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    marginTop: 4,
                  }}
                >
                  {option.tag}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Helper text */}
      <p style={{ ...FREDOKA, color: "#94A3B8", fontSize: 13, textAlign: "center", margin: 0 }}>
        This helps us recommend the best stories.
      </p>

      {portalTarget && ReactDOM.createPortal(continueButton, portalTarget)}
    </div>
  );
};

export default ChildAgeStep;
